/**
 * formulaGraph.js — CLIENT-SIDE NCalc parser + version export/import core (S1-P7).
 *
 * PURE ESM module: NO Vue / NO DOM / NO other-util imports → importable by plain Node.
 *
 * S1-P7 Blockly redesign (Wave 6): the Vue-Flow node/edge graph builders
 * (`graphToExpression`, `expressionToGraph`, `graphToFormulas`, `versionToGraph`,
 * `collectConstants`, `collectBrackets`) were RETIRED. The Blockly workspace is now the
 * single source of truth — compilation lives in `utils/blockly/ncalcGenerator.js` and the
 * blocks rebuild in `utils/blockly/ncalcToBlocks.js`. What remains here is the shared,
 * framework-agnostic surface those Blockly modules and the editor still depend on:
 *
 *   • tokenize / parseExpression / validateParsedArity — the S1-P3 NCalc grammar parser
 *     (used by ncalcToBlocks.js to walk a legacy `expression` back into blocks).
 *   • isSafeStringLiteral / sanitizeStringLiteral / MAX_STRING_LITERAL_LEN — the string-literal
 *     grammar (S1-P9 text compare), kept here beside the tokenizer that enforces it.
 *   • classifyField — map an identifier leaf to its source group badge (display-only; the
 *     `source` never changes the compiled string — the expression name always wins).
 *   • exportVersion / validateImport — whole-version export object + import shape/enum guard.
 *   • GRAPH_VERSION — the persisted graph-blob version (2 = Blockly workspace serialization).
 *
 * Grammar is exactly S1-P3: literal, identifier, `+ − × ÷`, `( )`, comparisons, `MIN,MAX,ROUND,IF`,
 * plus (S1-P9) a single-quoted STRING literal — comparison operand only, never a formula result —
 * plus (S1-P10) `SUMITEMS('<scope>', <body>)`, the per-item aggregate over a Tax Rule's entries.
 */

export const GRAPH_VERSION = 2;

// IFS(cond1, val1, cond2, val2, …, TRUE, default) — the multi-branch selector. Spelled once because
// the arity rule (EVEN, ≥2), the generator's emit and the paste diagnostics all key off it, and three
// copies of a function name is how they drift.
export const IFS_FN = 'IFS';

// SUM(<นิพจน์ที่มี []>) — the EXPLICIT reduction of an iterated computation to one number.
//
// A `[]` path iterates, and the per-iteration values are summed. WHERE that sum happens is the whole
// question: `MIN(INCOME_40_1.subItems[].amount * 0.5, 100000)` under a whole-expression implicit sum
// caps EVERY ROW at 100000 and then adds the caps up — which is not the §40(1) deduction anybody
// meant, and it is wrong by a number that looks perfectly plausible. So the boundary is only implicit
// when the iterated computation IS the whole expression; the moment a function wraps it, the author
// has to say which of the two readings they want (see analyzeIteration).
export const SUM_FN = 'SUM';

const FUNCS = new Set(['MIN', 'MAX', 'ROUND', 'IF', IFS_FN, SUM_FN]);
const APPLIES_TO_KINDS = ['RULE_CODE', 'VALUE_TYPE', 'GLOBAL'];

// ── RETIRED grammar — SUMITEMS and its three item-scope identifiers ────────────────────────────
//
// They were the per-item aggregate before `[]` paths existed. A path iterates and sums BY ITSELF, so
// they are gone — from the backend evaluator first (NCalcFormulaEvaluator.RetiredFunctions /
// RetiredIdentifiers), and from here now, with the SAME replacement spellings.
//
// They are RETIRED, not deleted: a formula stored before the retirement is still sitting in the
// database, and the one thing that must never happen when it is opened is that it quietly becomes
// something else — or dies as an anonymous "unknown function" the author cannot act on. Every path
// that used to accept them (parse, lint, paste, generate) now refuses them with the migration
// sentence below, which names what to write instead.
export const SUM_ITEMS_FN = 'SUMITEMS';

const RETIRED_FUNCTIONS = {
  [SUM_ITEMS_FN]:
    `${SUM_ITEMS_FN}() เลิกใช้แล้ว — ใช้ "เส้นทางข้อมูล" ที่มี [] แทน ซึ่งวนทุกรายการและบวกรวมให้เอง `
    + `เช่น ${SUM_ITEMS_FN}('income_40_5', item_amount * item_rate) เขียนใหม่เป็น `
    + 'INCOME_40_5.subItems[].items[].amount * INCOME_40_5.subItems[].rate',
};

// name → the path that replaces it (identical to the backend's RetiredIdentifiers table).
const RETIRED_IDENTIFIERS = {
  item_amount: 'INCOME_40_5.subItems[].items[].amount',
  item_rate: 'INCOME_40_5.subItems[].rate',
  item_expense: 'INCOME_40_5.actualExpense',
};

/** The retired names, for a caller that wants to list them (tests, tooltips). */
export const RETIRED_IDENTIFIER_NAMES = Object.keys(RETIRED_IDENTIFIERS);

/** Is `name` a retired function (case-insensitive, like the backend's OrdinalIgnoreCase table)? */
export function isRetiredFunction(name) {
  return Object.prototype.hasOwnProperty.call(RETIRED_FUNCTIONS, String(name == null ? '' : name).toUpperCase());
}

/** Is `name` one of the three retired item-scope identifiers? */
export function isRetiredIdentifier(name) {
  return Object.prototype.hasOwnProperty.call(RETIRED_IDENTIFIERS, String(name == null ? '' : name).toLowerCase());
}

/** The migration sentence for a retired FUNCTION, or '' when `name` is not one. */
export function retiredFunctionMessage(name) {
  return RETIRED_FUNCTIONS[String(name == null ? '' : name).toUpperCase()] || '';
}

/** The migration sentence for a retired IDENTIFIER, or '' when `name` is not one. */
export function retiredIdentifierMessage(name) {
  const key = String(name == null ? '' : name).toLowerCase();
  const replacement = RETIRED_IDENTIFIERS[key];
  if (!replacement) return '';
  return `ตัวแปร ${key} เลิกใช้แล้วพร้อมกับ ${SUM_ITEMS_FN}() — เขียนเป็นเส้นทางข้อมูล "${replacement}" แทน `
    + '([] วนทุกรายการและบวกรวมให้เอง)';
}

// ── TRUE / FALSE — KEYWORDS, not catalog names ────────────────────────────────────────────────
// They exist because IFS needs a final always-matching branch (`IFS(…, TRUE, 0)`), which is how the
// author writes "otherwise". Resolved in the PARSER rather than the tokenizer so an identifier is
// still just an identifier to `tokenize` — and matched case-insensitively because the C# evaluator's
// own literal handling is (NCalc parses true/True/TRUE alike), so a spelling the engine accepts must
// never read here as a variable named `true` that no catalog can resolve.
const BOOL_LITERALS = { TRUE: true, FALSE: false };

// ──────────────────────────────────────────────────────────────────────────────
//  exportVersion — whole-version export object (mirror of the import shape)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Build the whole-version export object (mirror of the import shape). Shape only — no eval.
 * @param {Object} full   { version, formulas[], brackets[], constants[] }
 * @param {Object} [workspaceBlob]  Blockly.serialization.workspaces.save output
 * @returns {Object}
 */
export function exportVersion(full, workspaceBlob) {
  const f = full || {};
  const v = f.version || {};
  const formulas = (Array.isArray(f.formulas) ? f.formulas : []).map((x) => ({
    appliesToKind: x.appliesToKind != null ? x.appliesToKind : null,
    appliesToKey: x.appliesToKey != null ? x.appliesToKey : null,
    expression: x.expression != null ? String(x.expression) : '',
    outputVar: x.outputVar != null ? String(x.outputVar) : '',
    evalOrder: x.evalOrder != null ? Number(x.evalOrder) : 0,
    notes: x.notes != null ? x.notes : null,
  }));
  const brackets = (Array.isArray(f.brackets) ? f.brackets : []).map((x) => ({
    lowerBound: num(x.lowerBound),
    upperBound: x.upperBound == null ? null : num(x.upperBound),
    rate: num(x.rate),
    sortOrder: num(x.sortOrder),
  }));
  const constants = (Array.isArray(f.constants) ? f.constants : []).map((x) => ({
    constKey: x.constKey != null ? String(x.constKey) : '',
    constValue: num(x.constValue),
    notes: x.notes != null ? x.notes : null,
  }));
  return {
    schema: 'tax-calc-version',
    graphVersion: GRAPH_VERSION,
    version: {
      versionCode: v.versionCode != null ? v.versionCode : null,
      description: v.description != null ? v.description : null,
    },
    formulas,
    brackets,
    constants,
    // S1-P7 Blockly redesign: canvasGraph now carries the Blockly workspace
    // serialization (Blockly.serialization.workspaces.save) wrapped as
    // { graphVersion: 2, workspace } — replaces the retired Vue-Flow node/edge shape.
    canvasGraph: workspaceBlob
      ? { graphVersion: GRAPH_VERSION, workspace: workspaceBlob }
      : null,
  };
}

/**
 * Shape / enum / required validation of an imported object BEFORE overwrite.
 * Does NOT verify that references (rule codes / variables) exist in the target version — shape only.
 * @param {Object} obj
 * @returns {{ ok:boolean, errors:string[] }}
 */
export function validateImport(obj) {
  const errors = [];
  const warnings = [];
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return { ok: false, errors: ['ไฟล์ไม่ใช่ออบเจ็กต์ JSON ที่ถูกต้อง'], warnings };
  }
  if (obj.graphVersion != null) {
    const gv = Number(obj.graphVersion);
    if (gv === 1) {
      warnings.push('ไฟล์เป็นรูปแบบเก่า (graphVersion 1 — Vue-Flow) — จะสร้าง layout ใหม่จากสูตร (expression เป็นตัวตัดสิน)');
    } else if (gv !== GRAPH_VERSION) {
      errors.push('graphVersion ไม่รองรับ: ' + obj.graphVersion + ' (รองรับ 1 หรือ ' + GRAPH_VERSION + ')');
    }
  }
  if (obj.version == null || typeof obj.version !== 'object' || Array.isArray(obj.version)) {
    errors.push('ขาด version meta (object)');
  }
  if (!Array.isArray(obj.formulas)) {
    errors.push('formulas ต้องเป็น array');
  } else {
    obj.formulas.forEach((x, i) => {
      if (x == null || typeof x !== 'object') { errors.push('formulas[' + i + '] ไม่ใช่ object'); return; }
      if (typeof x.expression !== 'string' || x.expression.trim() === '') errors.push('formulas[' + i + '].expression ต้องเป็น string ไม่ว่าง');
      if (typeof x.outputVar !== 'string' || x.outputVar.trim() === '') errors.push('formulas[' + i + '].outputVar ต้องเป็น string ไม่ว่าง');
      if (x.appliesToKind != null && APPLIES_TO_KINDS.indexOf(String(x.appliesToKind)) === -1) errors.push('formulas[' + i + '].appliesToKind ไม่ถูกต้อง: ' + x.appliesToKind);
      if (x.evalOrder != null && !Number.isFinite(Number(x.evalOrder))) errors.push('formulas[' + i + '].evalOrder ต้องเป็นตัวเลข');
    });
  }
  if (!Array.isArray(obj.brackets)) {
    errors.push('brackets ต้องเป็น array');
  } else {
    obj.brackets.forEach((x, i) => {
      if (x == null || typeof x !== 'object') { errors.push('brackets[' + i + '] ไม่ใช่ object'); return; }
      if (!Number.isFinite(Number(x.lowerBound))) errors.push('brackets[' + i + '].lowerBound ต้องเป็นตัวเลข');
      if (x.upperBound != null && !Number.isFinite(Number(x.upperBound))) errors.push('brackets[' + i + '].upperBound ต้องเป็นตัวเลขหรือ null');
      if (!Number.isFinite(Number(x.rate))) errors.push('brackets[' + i + '].rate ต้องเป็นตัวเลข');
    });
  }
  if (!Array.isArray(obj.constants)) {
    errors.push('constants ต้องเป็น array');
  } else {
    obj.constants.forEach((x, i) => {
      if (x == null || typeof x !== 'object') { errors.push('constants[' + i + '] ไม่ใช่ object'); return; }
      if (typeof x.constKey !== 'string' || x.constKey.trim() === '') errors.push('constants[' + i + '].constKey ต้องเป็น string ไม่ว่าง');
      if (!Number.isFinite(Number(x.constValue))) errors.push('constants[' + i + '].constValue ต้องเป็นตัวเลข');
    });
  }
  if (obj.canvasGraph != null) {
    const cg = obj.canvasGraph;
    if (typeof cg !== 'object' || Array.isArray(cg)) {
      errors.push('canvasGraph ต้องเป็น object');
    } else {
      const cgv = Number(cg.graphVersion != null ? cg.graphVersion : cg.version);
      if (cgv === GRAPH_VERSION) {
        // v2 = Blockly workspace serialization blob
        if (cg.workspace == null || typeof cg.workspace !== 'object' || Array.isArray(cg.workspace)) {
          errors.push('canvasGraph.workspace ต้องเป็น object (Blockly workspace)');
        }
      } else if (cgv === 1 || Array.isArray(cg.nodes) || Array.isArray(cg.edges)) {
        // retired Vue-Flow node/edge layout — accepted, rebuilt from formulas (expression wins)
        warnings.push('canvasGraph เป็นรูปแบบเก่า (v1 node/edge) — จะสร้าง layout ใหม่จากสูตร');
      } else {
        warnings.push('canvasGraph ไม่รู้จักรูปแบบ layout — จะสร้าง layout ใหม่จากสูตร');
      }
    }
  }
  return { ok: errors.length === 0, errors: dedupe(errors), warnings: dedupe(warnings) };
}

// ──────────────────────────────────────────────────────────────────────────────
//  String-literal grammar (S1-P9 text compare) — the sandbox stays a sandbox
// ──────────────────────────────────────────────────────────────────────────────
// ONE home for the rule. `tokenize` enforces it on the way IN (parsing a persisted expression),
// ncalcGenerator's tax_text emit and taxBlocks' TEXT field validator enforce the SAME rule on the way
// OUT, and the backend evaluator re-validates at save. Widening one copy without the others reopens
// the hole this grammar exists to keep shut — so all three read these two functions, never their own regex.
//
//   • SINGLE quotes only. A double-quoted literal stays an "unexpected character" → exactly one way
//     to write a literal, and `"` never becomes a character the sandbox has to reason about.
//   • Charset = letters · combining marks · decimal digits · space · `_ - . /`.
//     \p{M} is REQUIRED, not decoration: Thai sara/tone marks are COMBINING marks, so 'หญิง' is
//     L,L,M,L — a \p{L}-only class would silently reject the Thai values this feature exists for.
//   • NO backslash, NO escape sequences → a literal can never smuggle its own closing quote back in,
//     which is what makes "the next quote ends it" a safe tokenizer rule.
//   • Length cap 100: these are enum-ish values ('FEMALE', '90', 'หญิง'), so 100 is far above any real
//     one, while keeping a single literal from eating the 1000-char expression budget (MAX_EXPRESSION_LEN).
export const MAX_STRING_LITERAL_LEN = 100;
const STRING_LITERAL_CHARSET = /^[\p{L}\p{M}\p{Nd} _\-./]+$/u;
const STRING_LITERAL_UNSAFE_G = /[^\p{L}\p{M}\p{Nd} _\-./]/gu;

/**
 * Is `text` legal INSIDE a pair of single quotes? (The quotes themselves are never part of it.)
 * Empty is NOT safe: `''` compares against nothing, and allowing it makes an empty literal
 * indistinguishable from a mistyped quote pair.
 * @param {string} text  the literal's content, unquoted
 * @returns {boolean}
 */
export function isSafeStringLiteral(text) {
  const s = String(text == null ? '' : text);
  return s.length > 0 && s.length <= MAX_STRING_LITERAL_LEN && STRING_LITERAL_CHARSET.test(s);
}

/**
 * Coerce arbitrary typed/pasted text to the literal charset (drop what is not allowed, cap the length)
 * — the tax_text field validator's rule, so an author cannot type a quote or a backslash into a block
 * in the first place rather than discovering it as a backend rejection at save.
 * @param {string} text
 * @returns {string} always `isSafeStringLiteral()`-clean, or '' when nothing survives
 */
export function sanitizeStringLiteral(text) {
  return String(text == null ? '' : text).replace(STRING_LITERAL_UNSAFE_G, '').slice(0, MAX_STRING_LITERAL_LEN);
}

// ──────────────────────────────────────────────────────────────────────────────
//  Pratt / recursive-descent parser over the S1-P3 grammar
// ──────────────────────────────────────────────────────────────────────────────

export function parseExpression(input) {
  const tokens = tokenize(input);
  let pos = 0;
  // How many SUM() arguments we are currently INSIDE. A SUM inside a SUM has no reading: the inner one
  // already reduced its rows to one number, so the outer one would be summing a single value — which
  // means the author wrote two boundaries for one loop and only one of them can be what they meant.
  let sumDepth = 0;

  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (type, value) => {
    const t = tokens[pos];
    if (!t || t.type !== type || (value != null && t.value !== value)) {
      throw new Error('parse error: expected ' + (value || type) + ' near "' + (t ? t.value : '<eof>') + '"');
    }
    return tokens[pos++];
  };

  // comparison (lowest) → additive → multiplicative → unary → primary
  function parseComparison() {
    let left = parseAdditive();
    const t = peek();
    if (t && t.type === 'cmp') {
      next();
      const right = parseAdditive();
      return { kind: 'cmp', cmp: t.cmp, left, right };
    }
    return left;
  }

  function parseAdditive() {
    let left = parseMultiplicative();
    while (peek() && peek().type === 'op' && (peek().value === '+' || peek().value === '-')) {
      const op = next().value === '+' ? 'ADD' : 'SUB';
      const right = parseMultiplicative();
      left = { kind: 'op', op, left, right };
    }
    return left;
  }

  function parseMultiplicative() {
    let left = parseUnary();
    while (peek() && peek().type === 'op' && (peek().value === '*' || peek().value === '/')) {
      const op = next().value === '*' ? 'MUL' : 'DIV';
      const right = parseUnary();
      left = { kind: 'op', op, left, right };
    }
    return left;
  }

  function parseUnary() {
    const t = peek();
    if (t && t.type === 'op' && t.value === '-') {
      // Unary minus → 0 - x (keeps grammar binary-only; NCalc accepts it).
      next();
      const operand = parseUnary();
      return { kind: 'op', op: 'SUB', left: { kind: 'num', value: 0 }, right: operand };
    }
    if (t && t.type === 'op' && t.value === '+') { next(); return parseUnary(); }
    return parsePrimary();
  }

  function parsePrimary() {
    const t = peek();
    if (!t) throw new Error('parse error: unexpected end of expression');
    if (t.type === 'number') { next(); return { kind: 'num', value: t.value }; }
    // A string literal is a LEAF like a number — it parses anywhere an operand may sit. Which sockets
    // actually accept one is a BLOCK-level type check (tax_compare yes, tax_arith no), not a grammar
    // rule: the parser stays permissive so a rejected shape is reported by the thing that knows why.
    if (t.type === 'str') { next(); return { kind: 'str', value: t.value }; }
    if (t.type === 'lparen') {
      next();
      const inner = parseComparison();
      expect('rparen');
      return inner; // parens grouping is structural — compile re-derives parens by precedence
    }
    // A PATH is a LEAF exactly like an identifier — never a callee, never indexable, never further
    // navigable. The two refusals below are what keep it a NAME instead of an expression.
    if (t.type === 'path') {
      next();
      if (peek() && peek().type === 'lparen') {
        throw new Error('parse error: "' + t.value + '(" — a path is a name, not a function');
      }
      return { kind: 'path', name: t.value, segments: t.segments.map((g) => ({ ...g })) };
    }
    if (t.type === 'ident') {
      const kw = BOOL_LITERALS[t.value.toUpperCase()];
      if (kw !== undefined && !(tokens[pos + 1] && tokens[pos + 1].type === 'lparen')) {
        next();
        return { kind: 'bool', value: kw };
      }
      next();
      if (peek() && peek().type === 'lparen') {
        const fn = t.value.toUpperCase();
        // RETIRED first, so a stored SUMITEMS formula reports the migration instruction rather than
        // dying as "unknown function" — a message that names neither what happened nor what to write.
        if (isRetiredFunction(fn)) throw new Error(retiredFunctionMessage(fn));
        if (!FUNCS.has(fn)) throw new Error('parse error: unknown function ' + t.value + '()');
        const isSum = fn === SUM_FN;
        if (isSum && sumDepth > 0) {
          throw new Error('parse error: ' + SUM_FN + '() cannot be nested inside another ' + SUM_FN + '()');
        }
        if (isSum) sumDepth += 1;
        next(); // consume '('
        const args = [];
        if (!(peek() && peek().type === 'rparen')) {
          args.push(parseComparison());
          while (peek() && peek().type === 'comma') { next(); args.push(parseComparison()); }
        }
        if (isSum) sumDepth -= 1;
        expect('rparen');
        validateParsedArity(fn, args.length);
        return { kind: 'func', fn, args };
      }
      // A retired item-scope name is not a variable any more — it is a name the evaluator refuses at
      // BOTH save and run time. Failing here is what makes an old formula openable-with-a-reason
      // instead of loadable-and-silently-broken.
      if (isRetiredIdentifier(t.value)) throw new Error(retiredIdentifierMessage(t.value));
      return { kind: 'id', name: t.value };
    }
    throw new Error('parse error: unexpected token "' + t.value + '"');
  }

  const ast = parseComparison();
  if (pos < tokens.length) {
    throw new Error('parse error: trailing token "' + tokens[pos].value + '"');
  }
  return ast;
}

export function validateParsedArity(fn, n) {
  if ((fn === 'MIN' || fn === 'MAX') && n < 2) throw new Error('parse error: ' + fn + '() needs ≥2 args');
  // EVEN, ≥2 — the arguments are (condition, value) PAIRS. An odd count means one pair lost its value
  // (or gained a stray one), and NCalc would happily evaluate the truncated call: the last branch would
  // silently become a condition with no result, which is a wrong number with no error anywhere.
  if (fn === IFS_FN && (n < 2 || n % 2 !== 0)) throw new Error('parse error: ' + IFS_FN + '() needs an EVEN number of args (condition, value pairs)');
  if (fn === 'ROUND' && (n < 1 || n > 2)) throw new Error('parse error: ROUND() needs 1–2 args');
  if (fn === 'IF' && n !== 3) throw new Error('parse error: IF() needs exactly 3 args');
  // EXACTLY 1 — SUM reduces ONE iterated computation. `SUM(a[], b[])` reads like a two-argument total,
  // which is a different (and unimplementable) thing: two loops with no stated nesting. Writing
  // `SUM(a[]) + SUM(b[])` says which, and is refused elsewhere if the two do not correlate.
  if (fn === SUM_FN && n !== 1) {
    throw new Error('parse error: ' + SUM_FN + '() needs exactly 1 arg — ' + SUM_FN + '(<นิพจน์ต่อรายการ>)');
  }
}

export function tokenize(input) {
  const tokens = [];
  let i = 0;
  const s = input;
  const isDigit = (c) => c >= '0' && c <= '9';
  // ANCHORED and single-character on purpose. Unanchored `/[A-Za-z_]/.test(c)` is TRUE for `undefined`
  // (it stringifies to "undefined", which contains letters), so a lookahead one past the end of the
  // input read as "there is an identifier there" — which is exactly how a trailing `a.` used to parse
  // as a path with an EMPTY final segment instead of failing.
  const isIdentStart = (c) => c != null && /^[A-Za-z_]$/.test(c);
  const isIdentPart = (c) => c != null && /^[A-Za-z0-9_]$/.test(c);
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i += 1; continue; }
    // number: digits with optional single decimal point, or leading '.'
    if (isDigit(c) || (c === '.' && isDigit(s[i + 1]))) {
      let j = i; let seenDot = false;
      while (j < s.length && (isDigit(s[j]) || (s[j] === '.' && !seenDot))) {
        if (s[j] === '.') seenDot = true;
        j += 1;
      }
      tokens.push({ type: 'number', value: Number(s.slice(i, j)) });
      i = j; continue;
    }
    // string literal: 'FEMALE' — single quotes ONLY. No escape sequences exist in this grammar, so the
    // NEXT quote always closes the literal; an unterminated or off-charset one is a parse error, never a
    // best-effort salvage (the whole point is that what the block editor shows is what the engine runs).
    if (c === "'" || c === '"') {
      // BOTH quote styles open a literal. Double quotes were an "unexpected character" until the
      // nested-path grammar arrived writing `subTypeCode="BUILDING_30"`, which is how every author and
      // every LLM spells a string. The CANONICAL spelling stays SINGLE (ncalcGenerator emits `'…'`),
      // so there is still exactly one way a literal is ever PERSISTED — accepting `"` widens what can
      // be READ, not what is written, and the charset below is unchanged either way.
      const close = s.indexOf(c, i + 1);
      if (close === -1) throw new Error('parse error: unterminated string literal at ' + i);
      const raw = s.slice(i + 1, close);
      if (!isSafeStringLiteral(raw)) {
        throw new Error('parse error: invalid string literal \'' + raw + '\' (allowed: letters/digits/space/_-./ , 1–' + MAX_STRING_LITERAL_LEN + ' chars)');
      }
      tokens.push({ type: 'str', value: raw });
      i = close + 1; continue;
    }
    if (isIdentStart(c)) {
      let j = i + 1;
      while (j < s.length && isIdentPart(s[j])) j += 1;
      // A PATH continues an identifier with `.segment` and/or `[]` — `INCOME_40_5.subItems[].amount`.
      // NO whitespace is tolerated anywhere inside it, and that is the point: a path is a NAME, so it
      // has exactly ONE spelling and cannot be assembled out of operators. Everything that is not a
      // bare segment name or a bare `[]` is refused HERE, with the reason, rather than surviving as a
      // token sequence the parser would have to reconstruct intent from.
      const segs = [{ name: s.slice(i, j), iter: false }];
      let isPath = false;
      for (;;) {
        if (s[j] === '.') {
          if (!isIdentStart(s[j + 1])) break; // `a.` / `a..b` / `a.5` — not a path continuation
          let k = j + 2;
          while (k < s.length && isIdentPart(s[k])) k += 1;
          segs.push({ name: s.slice(j + 1, k), iter: false });
          j = k; isPath = true; continue;
        }
        if (s[j] === '[') {
          if (s[j + 1] !== ']') {
            // `items[0]`, `items[i]`, `items[` — an INDEX. Allowing one would make a path an
            // expression with a computable position, which is the whole thing the sandbox forbids.
            throw new Error('parse error: index inside [ ] is not allowed at ' + j
              + " — write [] to mean \"every item\" (e.g. subItems[].amount)");
          }
          if (segs[segs.length - 1].iter) {
            throw new Error('parse error: repeated [][] at ' + j + ' — one [] per segment');
          }
          segs[segs.length - 1].iter = true;
          j += 2; isPath = true; continue;
        }
        break;
      }
      if (isPath) {
        tokens.push({ type: 'path', value: pathText(segs), segments: segs });
      } else {
        tokens.push({ type: 'ident', value: segs[0].name });
      }
      i = j; continue;
    }
    // `[` that did not follow a segment name (`[].x`, `[INC_40_1]`) — named, because the bare
    // "unexpected character" it used to be says nothing about the one legal use of a bracket.
    if (c === '[' || c === ']') {
      throw new Error('parse error: "' + c + '" at ' + i
        + ' — [ ] is only legal as the empty pair [] directly after a path segment (subItems[])');
    }
    // two-char comparators first
    const two = s.slice(i, i + 2);
    if (two === '<=') { tokens.push({ type: 'cmp', cmp: 'LE', value: '<=' }); i += 2; continue; }
    if (two === '>=') { tokens.push({ type: 'cmp', cmp: 'GE', value: '>=' }); i += 2; continue; }
    if (two === '==') { tokens.push({ type: 'cmp', cmp: 'EQ', value: '==' }); i += 2; continue; }
    if (two === '!=') { tokens.push({ type: 'cmp', cmp: 'NE', value: '!=' }); i += 2; continue; }
    if (two === '<>') { tokens.push({ type: 'cmp', cmp: 'NE', value: '<>' }); i += 2; continue; }
    if (c === '<') { tokens.push({ type: 'cmp', cmp: 'LT', value: '<' }); i += 1; continue; }
    if (c === '>') { tokens.push({ type: 'cmp', cmp: 'GT', value: '>' }); i += 1; continue; }
    if (c === '=') { tokens.push({ type: 'cmp', cmp: 'EQ', value: '=' }); i += 1; continue; }
    if (c === '+' || c === '-' || c === '*' || c === '/') { tokens.push({ type: 'op', value: c }); i += 1; continue; }
    if (c === '(') { tokens.push({ type: 'lparen', value: '(' }); i += 1; continue; }
    if (c === ')') { tokens.push({ type: 'rparen', value: ')' }); i += 1; continue; }
    if (c === ',') { tokens.push({ type: 'comma', value: ',' }); i += 1; continue; }
    throw new Error('parse error: unexpected character "' + c + '" at ' + i);
  }
  return tokens;
}

// ──────────────────────────────────────────────────────────────────────────────
//  PATH — `ROOT.segment[].segment` : a NAME with iteration points
// ──────────────────────────────────────────────────────────────────────────────
//
// `[]` means "for every item at this position". Paths that share a `[]`-prefix iterate TOGETHER on the
// SAME index — never a cross product — so in
//
//     INCOME_40_5.subItems[].items[].amount * IFS(INCOME_40_5.subItems[].subTypeCode = 'BUILDING_30', …)
//
// the `subItems[]` on the left and the `subItems[]` inside the IFS are the SAME subItem, and `items[]`
// is the inner loop within it. The whole formula produces one value per iteration TUPLE and those values
// are SUMMED (the only reduction that makes sense for money, and what §40(5) needs: total expense = Σ
// over rows). Correlation and the sum both happen in the ENGINE — this module only has to make sure the
// path text that reaches it is exact, because a path that loses or gains a `[]` silently changes which
// rows are multiplied together.
//
// The parts below are therefore about SPELLING, and they are strict on purpose: one canonical form
// (no whitespace, no index, no trailing dot) so the same path always compares equal to itself.

/** Canonical text of a segment list: `a.b[].c` (no whitespace — a path has ONE spelling). */
function pathText(segs) {
  return (segs || []).map((g, i) => (i === 0 ? g.name : '.' + g.name) + (g.iter ? '[]' : '')).join('');
}

/** Does `name` LOOK like a path (as opposed to a plain identifier)? Cheap shape test, no parsing. */
export function isPathName(name) {
  const n = String(name == null ? '' : name);
  return n.indexOf('.') !== -1 || n.indexOf('[') !== -1;
}

/**
 * Parse a path NAME into its segments, or null when it is not a well-formed path.
 *
 * Goes through the real `tokenize`, so "what the blocks accept" and "what the parser accepts" cannot
 * drift: a path the editor can build is by construction a path the grammar reads back. A plain
 * identifier returns null (it is not a path), and so does anything with an index, a trailing dot,
 * whitespace or a second token after it.
 * @param {string} name
 * @returns {?Array<{name:string, iter:boolean}>}
 */
export function parsePathName(name) {
  const n = String(name == null ? '' : name);
  let toks;
  try { toks = tokenize(n); } catch (_err) { return null; }
  if (toks.length !== 1 || toks[0].type !== 'path') return null;
  return toks[0].segments.map((g) => ({ ...g }));
}

/** The ROOT segment of a path (`INCOME_40_5` of `INCOME_40_5.subItems[].amount`); '' when unparseable. */
export function pathRoot(name) {
  const segs = parsePathName(name);
  return segs ? segs[0].name : '';
}

/**
 * A path spelled for a HUMAN: `INCOME_40_5 ▸ subItems[] ▸ items[] ▸ amount`.
 * DISPLAY ONLY — never compiled, never persisted (see tax_path's saveExtraState, which stores the
 * canonical text). The `[]` is KEPT, because it is the load-bearing part: it is the difference between
 * one row and every row, and a reader who cannot see it cannot check the formula.
 */
export function pathDisplay(name) {
  const segs = parsePathName(name);
  if (!segs) return String(name == null ? '' : name);
  return segs.map((g) => g.name + (g.iter ? '[]' : '')).join(' ▸ ');
}

// ──────────────────────────────────────────────────────────────────────────────
//  analyzeIteration — the two STRUCTURAL rules about `[]`, checked with no data
// ──────────────────────────────────────────────────────────────────────────────
//
// Both are decided by the SHAPE of the expression alone, which is why they belong here (one
// implementation, shared by the generator, the lint and the paste dialog) and why they can be
// reported to the author BEFORE they save rather than as a 400 afterwards.
//
//   1. CORRELATION — every `[]` in the expression contributes its PREFIX, and those prefixes must
//      form a single nesting CHAIN. Two `[]` points that are not nested inside one another have no
//      correlated reading: pairing them is a cross product that multiplies unrelated tax rows
//      together and produces a plausible, badly wrong figure. Mirrors the backend's
//      NCalcFormulaEvaluator.BuildIterationChain, message included.
//
//   2. WHERE THE SUM HAPPENS — see SUM_FN. The implicit whole-expression sum is kept ONLY when the
//      iterated computation IS the whole expression. If a FUNCTION wraps it (every `[]` of the scope
//      sits inside that one call), the per-row and the on-the-total readings differ and both are
//      ordinary things to want, so the author must write which — with SUM.
//      Inside an explicit `SUM(…)` the question is already answered, so the rule does not apply there.
//
// Everything below reads the parser AST (`parseExpression`), never the text: a literal spelling
// `subItems[]` must stay inert, and only a tree can guarantee that.

/** Every node of the AST, parents first. */
function astNodes(node, out) {
  if (!node || typeof node !== 'object') return out;
  out.push(node);
  if (node.kind === 'func') (node.args || []).forEach((a) => astNodes(a, out));
  else { astNodes(node.left, out); astNodes(node.right, out); }
  return out;
}

/** Canonical text of `segs[0..upTo]` — the prefix an iteration point is identified by. */
function renderPrefix(segs, upTo) {
  return segs.slice(0, upTo + 1)
    .map((g, i) => (i === 0 ? g.name : '.' + g.name) + (g.iter ? '[]' : ''))
    .join('');
}

/** The `[]` prefixes a single path contributes, outermost first. */
function iterationPrefixesOf(pathNode) {
  const segs = (pathNode && pathNode.segments) || [];
  const out = [];
  segs.forEach((g, i) => { if (g.iter) out.push({ depth: i + 1, text: renderPrefix(segs, i) }); });
  return out;
}

/** Does `a` (an iteration prefix) nest inside `b`? Textual prefix test on the canonical spelling. */
function prefixNests(a, b) {
  return b.length > a.length && b.slice(0, a.length).toLowerCase() === a.toLowerCase();
}

/**
 * The correlation rule — the exact structure the backend builds, so the two cannot disagree about
 * which formulas are savable.
 * @returns {string[]} at most one message
 */
function correlationErrors(ast) {
  const seen = new Set();
  const prefixes = [];
  astNodes(ast, []).forEach((n) => {
    if (n.kind !== 'path') return;
    iterationPrefixesOf(n).forEach((p) => {
      const key = p.text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      prefixes.push(p);
    });
  });
  // Stable sort by depth: equal-depth siblings keep first-appearance order, so the message names them
  // in the order the author reads them.
  const chain = prefixes.slice().sort((a, b) => a.depth - b.depth);
  for (let i = 1; i < chain.length; i += 1) {
    if (prefixNests(chain[i - 1].text, chain[i].text)) continue;
    return [
      `"${chain[i - 1].text}" กับ "${chain[i].text}" เป็นการวน [] สองชุดที่ไม่ได้ซ้อนอยู่ในกันและกัน `
      + 'จึงไม่มีวิธีจับคู่รายการของทั้งสองฝั่งนอกจากจับคู่ข้ามกันทั้งหมด (cross product) '
      + 'ซึ่งจะเอารายการที่ไม่เกี่ยวกันมาคูณกัน — ให้แยกเป็นสองสูตร หรือทำให้เส้นทางหนึ่งซ้อนอยู่ในอีกเส้นทางหนึ่ง',
    ];
  }
  return [];
}

/** Does this subtree contain a `[]` path that belongs to the CURRENT scope (SUM subtrees excluded)? */
function iteratesInScope(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.kind === 'path') return (node.segments || []).some((g) => g.iter);
  if (node.kind === 'func') {
    // An explicit SUM has already reduced its rows to one number — from the enclosing scope's point of
    // view it is a scalar, which is exactly what makes `MIN(SUM(p[] * 0.5), 100000)` legal.
    if (String(node.fn).toUpperCase() === SUM_FN) return false;
    return (node.args || []).some(iteratesInScope);
  }
  return iteratesInScope(node.left) || iteratesInScope(node.right);
}

/** Count the scope's `[]` path nodes under `node` (SUM subtrees excluded). */
function countScopeIterators(node) {
  if (!node || typeof node !== 'object') return 0;
  if (node.kind === 'path') return (node.segments || []).some((g) => g.iter) ? 1 : 0;
  if (node.kind === 'func') {
    if (String(node.fn).toUpperCase() === SUM_FN) return 0;
    return (node.args || []).reduce((n, a) => n + countScopeIterators(a), 0);
  }
  return countScopeIterators(node.left) + countScopeIterators(node.right);
}

/** Every non-SUM function node of the scope (SUM subtrees are their own scope). */
function scopeFunctions(node, out) {
  if (!node || typeof node !== 'object') return out;
  if (node.kind === 'func') {
    if (String(node.fn).toUpperCase() === SUM_FN) return out;
    out.push(node);
    (node.args || []).forEach((a) => scopeFunctions(a, out));
    return out;
  }
  scopeFunctions(node.left, out);
  scopeFunctions(node.right, out);
  return out;
}

/** Every `SUM(...)` node anywhere in the tree. */
function sumNodes(node, out) {
  if (!node || typeof node !== 'object') return out;
  if (node.kind === 'func') {
    if (String(node.fn).toUpperCase() === SUM_FN) out.push(node);
    (node.args || []).forEach((a) => sumNodes(a, out));
    return out;
  }
  sumNodes(node.left, out);
  sumNodes(node.right, out);
  return out;
}

/**
 * The implicit-sum rule, applied to ONE scope (the whole expression — an explicit SUM's argument is
 * not a scope this runs on, by design: writing SUM is how the author answers this very question).
 * @returns {string[]}
 */
function implicitSumErrors(scopeRoot) {
  const total = countScopeIterators(scopeRoot);
  if (total === 0) return [];
  // A function WRAPS the iterated computation when every `[]` of the scope sits inside that one call.
  // §40(5) — `A.subItems[].items[].amount * IFS(A.subItems[].subTypeCode = 'X', 0.3, TRUE, 0)` — has
  // its two `[]` on opposite sides of the `*`, so NO function wraps them both: the iterated
  // computation is the whole expression and the implicit sum is the only reading. Whereas
  // `MIN(A.subItems[].amount * 0.5, 100000)` has its single `[]` entirely inside MIN.
  const wrapper = scopeFunctions(scopeRoot, []).find((f) => countScopeIterators(f) === total);
  if (!wrapper) return [];
  const fn = String(wrapper.fn).toUpperCase();
  return [
    `[] ทั้งหมดอยู่ข้างใน ${fn}(…) — ต้องบอกให้ชัดว่า "รวมทุกรายการ" เกิดก่อนหรือหลัง ${fn} `
    + `เพราะสองแบบให้คนละคำตอบ: เขียน ${fn}(${SUM_FN}(<สูตรต่อรายการ>), …) ถ้าจะรวมทุกรายการก่อนแล้วค่อย ${fn} `
    + `หรือ ${SUM_FN}(${fn}(<สูตรต่อรายการ>, …)) ถ้าจะ ${fn} ทีละรายการแล้วค่อยรวม`,
  ];
}

/** A `SUM()` with nothing to sum — the author lost a `[]`, and `SUM(x)` would quietly return `x`. */
function emptySumErrors(ast) {
  const bad = sumNodes(ast, []).filter((s) => !countScopeIterators((s.args || [])[0]));
  if (!bad.length) return [];
  return [
    `${SUM_FN}() ข้างในไม่มีเส้นทางข้อมูลที่มี [] เลย จึงไม่มีอะไรให้รวม — `
    + `${SUM_FN}(x) มีค่าเท่ากับ x เฉย ๆ ให้เอา ${SUM_FN}() ออก หรือใส่เส้นทางข้อมูลที่มี [] เข้าไป`,
  ];
}

/**
 * Both structural `[]` rules over a parsed expression. PURE and data-free — the author learns before
 * saving, not from a 400 afterwards.
 * @param {Object} ast  a `parseExpression` result
 * @returns {string[]} human messages (empty = the shape is unambiguous)
 */
export function analyzeIteration(ast) {
  if (!ast || typeof ast !== 'object') return [];
  // Correlation FIRST: when two loops cannot be paired at all, "where does the sum happen" is a
  // question about an expression that has no reading to begin with.
  const corr = correlationErrors(ast);
  if (corr.length) return corr;
  return dedupe([...emptySumErrors(ast), ...implicitSumErrors(ast)]);
}

/** A refusal the SUM rules own, as opposed to an ordinary syntax error somewhere else in the text. */
const SUM_PARSE_REFUSAL = new RegExp('\\b' + SUM_FN + '\\b');

/**
 * Convenience wrapper: parse `expression` and run {@link analyzeIteration} on it.
 *
 * A string that does not parse yields NO iteration errors — it has a parse error, which is the
 * caller's to report, and stacking a structural complaint on top of it would only bury the real one.
 * The ONE exception is a throw that is itself one of these rules (a nested `SUM`, a wrong `SUM`
 * arity): the parser is where those live, so swallowing them here would make the lint silently accept
 * exactly what the paste dialog and the canvas refuse. `\bSUM\b` does not match SUMITEMS (no word
 * boundary before the I), so a retired-name throw still belongs to the caller.
 * @param {string} expression
 * @returns {string[]}
 */
export function analyzeIterationText(expression) {
  const src = String(expression == null ? '' : expression);
  // A `[` means an iteration point; a `SUM(` means a stated boundary (which must contain one).
  // Anything else cannot trip either rule, so it is not worth a parse.
  if (!/\[|\bSUM\s*\(/i.test(src)) return [];
  let ast;
  try {
    ast = parseExpression(src);
  } catch (err) {
    const msg = String((err && err.message) || '');
    return SUM_PARSE_REFUSAL.test(msg) ? [msg.replace(/^parse error:\s*/, '')] : [];
  }
  return analyzeIteration(ast);
}

// ──────────────────────────────────────────────────────────────────────────────
//  classifyField — identifier → source group badge (DISPLAY-ONLY; never compiled)
// ──────────────────────────────────────────────────────────────────────────────

/** The เหมา-rate half of a rule's bridge pair (`salary_40_1_rate`) — display grouping only. */
function isRateVar(name) {
  return /_rate$/i.test(String(name));
}

/**
 * Classify an identifier leaf into a source group for the palette badge.
 * `source` is DISPLAY-ONLY (never compiled). Unknown → flagged/stale.
 *
 * A name is stale when it resolves in NO catalog group — a rule dropped from the config version, a
 * ค่าคงที่ or a sibling สูตร deleted from the calc version, a personal/global name gone across
 * releases. The caller therefore owes this function a COMPLETE catalog: "not in the catalog" is read
 * as "deleted from the version", so classifying against a catalog that merely has not loaded yet
 * flags everything. The view gates on that (TaxFormulaBlockly → catalogComplete) before it lets a
 * `flagged` reach a block.
 *
 * @param {string} name
 * @param {Object} catalog  normalized { ruleFields, personalInfo, globals, constants, outputVars, ruleInputs, itemScope } (Sets)
 * @returns {{ source:string, flagged:boolean }}
 */
export function classifyField(name, catalog) {
  const cat = catalog && catalog.__normalized ? catalog : normalizeCatalog(catalog);
  const n = String(name);
  // A PATH is classified by its ROOT and nothing else. The catalog knows which tax rules exist; it does
  // NOT know the shape of the JSON hanging off one, and pretending otherwise would flag every legitimate
  // `.subItems[].amount` as deleted the moment a caller passed a rule list. So: an unknown ROOT is the
  // one thing that can be PROVEN wrong here and it is flagged; the segments below it are the engine's to
  // resolve at run time, and a wrong one shows up there (see the unmatched-branch warning), not here.
  if (isPathName(n)) {
    const root = pathRoot(n);
    if (root === '') return { source: 'Path', flagged: true }; // malformed spelling — never silently ok
    const rootCls = classifyField(root, cat);
    return { source: 'Path', flagged: rootCls.flagged };
  }
  // normalizeCatalog lower-cases every group, so one lower-cased probe resolves each of them.
  const lower = n.toLowerCase();
  // A RETIRED item-scope name resolves nowhere and must never look resolved: the evaluator refuses it
  // at both save and run time, so the block wearing it is broken and the "!" is the truth about it.
  // Checked ahead of every catalog group so a version that happens to mint a constant named item_rate
  // cannot launder one back into looking live.
  if (isRetiredIdentifier(n)) return { source: 'RuleFields', flagged: true };
  if (cat.constants.has(lower)) return { source: 'Constant', flagged: false };
  if (cat.outputVars.has(lower)) return { source: 'OutputVar', flagged: false };
  if (cat.personalInfo.has(lower)) return { source: 'PersonalInfo', flagged: false };
  if (cat.globals.has(lower)) return { source: 'Globals', flagged: false };
  // DEAD on the live per-formula path (TaxFormulaBlockly.classifyCatalog supplies no ruleFields —
  // the rule-fields catalog is gone). Kept for the RETIRED whole-version canvas, which still passes
  // one. It must never be the reason a stale name reads as resolved: only a name the caller actually
  // put in this group survives here.
  if (cat.ruleFields.has(lower)) {
    return { source: /^input/i.test(n) ? 'RuleInput' : 'RuleFields', flagged: false };
  }
  // per-rule bridge vars (lower-cased rule code, e.g. salary_40_1 + salary_40_1_rate) — the value the
  // user entered for that rule and the เหมา rate of the option chosen for it; the backend injects both
  // globally, so they are NOT stale. The `_rate` half gets its OWN source: a rate is not an amount, and
  // rebuilding a block from Expression must not collapse the two back together.
  //
  // This is the ONLY thing that resolves a rule bridge var, by design. A `/^income_/ → not stale`
  // fallback used to sit below it, from before ruleInputs was populated: ruleBridgeVars() now emits
  // `<code>` and `<code>_rate` for EVERY live rule, so the fallback resolved exactly the names that
  // are NOT live — i.e. it hid the deleted rule, the one case the ⚠/! marker exists to show.
  if (cat.ruleInputs && cat.ruleInputs.has(lower)) {
    return { source: isRateVar(n) ? 'RuleRate' : 'RuleInput', flagged: false };
  }
  // Off-catalog → stale. Source is the '?' bucket, NOT a rule/rate guess: an unresolvable name must
  // not keep posing as the thing it used to be. Still compiles to its own name (display-only).
  return { source: 'RuleFields', flagged: true };
}

// ──────────────────────────────────────────────────────────────────────────────
//  Helpers
// ──────────────────────────────────────────────────────────────────────────────

function normalizeCatalog(catalog) {
  // Lower-cased on BOTH sides of the lookup: the backend's allowed-variable check is
  // OrdinalIgnoreCase (NCalcFormulaEvaluator), and this view's own duplicate/cycle checks already
  // compare outputVars lower-cased — so a name the server accepts must never read as deleted here.
  // Callers hand these groups over un-normalized (constants UPPER, rule bridges lower, outputVars and
  // catalog names raw as the author typed them), which is exactly where a case split would come from.
  const toSet = (arr) => new Set((Array.isArray(arr) ? arr : [])
    .map((v) => (v && v.name != null ? String(v.name) : String(v)).toLowerCase()));
  const c = catalog || {};
  return {
    __normalized: true,
    ruleFields: toSet(c.ruleFields),
    personalInfo: toSet(c.personalInfo),
    globals: toSet(c.globals),
    constants: toSet(c.constants),
    outputVars: toSet(c.outputVars),
    ruleInputs: toSet(c.ruleInputs), // lower-cased rule-code bridge vars (salary_40_1, …)
  };
}

function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function dedupe(arr) {
  return Array.from(new Set(arr));
}

export default {
  GRAPH_VERSION,
  MAX_STRING_LITERAL_LEN,
  SUM_ITEMS_FN,
  IFS_FN,
  SUM_FN,
  RETIRED_IDENTIFIER_NAMES,
  isRetiredFunction,
  isRetiredIdentifier,
  retiredFunctionMessage,
  retiredIdentifierMessage,
  analyzeIteration,
  analyzeIterationText,
  isPathName,
  parsePathName,
  pathRoot,
  pathDisplay,
  classifyField,
  exportVersion,
  validateImport,
  tokenize,
  parseExpression,
  validateParsedArity,
  isSafeStringLiteral,
  sanitizeStringLiteral,
};
